import React, { useState, useEffect } from "react";
import { Send, Loader2, Sparkles } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function EnquiryForm({ selectedTechnology }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({ name: "", email: "", phone: "", technology: "", message: "" });

  useEffect(() => {
    if (selectedTechnology !== undefined && selectedTechnology !== null) {
      setForm((s) => ({ ...s, technology: selectedTechnology }));
    }
  }, [selectedTechnology]);

  const onChange = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await api.post("/enquiry", form);
      toast.success("Enquiry submitted. Our team will get back to you shortly.");
      setForm({ name: "", email: "", phone: "", technology: selectedTechnology || "", message: "" });
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const techLabel = (form.technology || "").trim();

  return (
    <form onSubmit={submit} className="vj-card p-7 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="enquiry-form">
      <div className="md:col-span-2">
        <div className="overline">Send Enquiry</div>
        <h2 className="font-display text-2xl font-extrabold tracking-tight mt-1">
          {techLabel ? `Enquiry for ${techLabel}` : "Send an Enquiry"}
        </h2>
        {techLabel && (
          <div className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-[#1E3A8A] bg-blue-50 border border-blue-100 px-3 py-1.5 rounded-full" data-testid="enquiry-tech-chip">
            <Sparkles size={13} /> Enquiry for: {techLabel}
          </div>
        )}
      </div>
      <Field label="Name" value={form.name} onChange={onChange("name")} required testid="enquiry-name" />
      <Field label="Email" type="email" value={form.email} onChange={onChange("email")} required testid="enquiry-email" />
      <Field label="Phone" value={form.phone} onChange={onChange("phone")} testid="enquiry-phone" />
      <Field
        label="Technology / Interest"
        value={form.technology}
        onChange={onChange("technology")}
        placeholder="e.g. Autonomous Surveillance Drones — or a custom requirement"
        testid="enquiry-technology"
      />
      <div className="md:col-span-2">
        <label className="overline">Your Enquiry</label>
        <textarea
          data-testid="enquiry-message"
          required
          value={form.message}
          onChange={onChange("message")}
          className="vj-input mt-1.5"
          placeholder="Describe your requirement, application area, scale, and any specifications."
        />
      </div>
      {err && <div className="md:col-span-2 text-sm text-red-600">{err}</div>}
      <div className="md:col-span-2">
        <button disabled={busy} className="vj-btn-primary" data-testid="enquiry-submit">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send Enquiry
        </button>
      </div>
    </form>
  );
}

function Field({ label, testid, className = "", ...props }) {
  return (
    <div className={className}>
      <label className="overline">{label}</label>
      <input data-testid={testid} className="vj-input mt-1.5" {...props} />
    </div>
  );
}
